import { Languages } from "lucide-react";
import { CollapsibleSessionBar } from "./CollapsibleSessionBar";

export interface TranslationLanguageOption {
  code: string;
  name: string;
}

interface TranslationControlsProps {
  enabled: boolean;
  languageCode: string;
  languageOptions: TranslationLanguageOption[];
  onEnabledChange: (enabled: boolean) => void;
  onLanguageChange: (languageCode: string) => void;
}

export function TranslationControls({
  enabled,
  languageCode,
  languageOptions,
  onEnabledChange,
  onLanguageChange,
}: TranslationControlsProps) {
  return (
    <CollapsibleSessionBar
      label="translations"
      collapsedLabel={`Translations · ${enabled ? "On" : "Off"}`}
      collapsedIcon={
        <Languages
          className={enabled ? "text-orange-300/70" : "text-white/30"}
          size={13}
          aria-hidden="true"
        />
      }
      leading={
        <div className="flex min-w-0 items-center gap-2 text-xs">
          <Languages
            className={enabled ? "text-orange-300" : "text-white/35"}
            size={14}
            aria-hidden="true"
          />
          <span className={enabled ? "font-semibold text-white/75" : "font-medium text-white/50"}>
            Translations
          </span>
          {enabled ? (
            <select
              aria-label="Translation language"
              value={languageCode}
              onChange={(event) => onLanguageChange(event.target.value)}
              className="max-w-[11rem] rounded-md border border-white/10 bg-white/[0.055] px-2 py-1 text-xs font-medium text-white/75 outline-none transition-colors hover:border-white/20 focus:border-orange-300/45 focus:ring-2 focus:ring-orange-300/15"
            >
              {languageOptions.map((option) => (
                <option key={option.code} value={option.code} className="bg-[#17171d] text-white">
                  {option.name}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-[11px] text-white/30">Off</span>
          )}
        </div>
      }
      trailing={
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label="Show tutor translations"
          onClick={() => onEnabledChange(!enabled)}
          className={`relative h-6 w-11 shrink-0 rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/45 ${
            enabled ? "border-orange-300/45 bg-orange-400/25" : "border-white/15 bg-white/[0.06]"
          }`}
        >
          <span
            className={`absolute top-0.5 h-[18px] w-[18px] rounded-full transition-all ${
              enabled ? "left-[1.25rem] bg-orange-300" : "left-0.5 bg-white/45"
            }`}
          />
        </button>
      }
    />
  );
}
